const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function getToken(): string | null {
  return localStorage.getItem('ericoder_token');
}

export function setToken(token: string): void {
  localStorage.setItem('ericoder_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('ericoder_token');
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown };

async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      window.location.href = '/login'; // Auto logout for single-device
    }
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string; message?: string; detail?: string };
    throw new Error(err.error ?? err.message ?? err.detail ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  login: (username: string, password: string) =>
    request<{ token: string; user: any }>('/api/auth/login', { method: 'POST', body: { username, password } }),

  register: (username: string, password: string, email?: string) =>
    request<{ token: string; user: any }>('/api/auth/register', { method: 'POST', body: { username, password, email } }),

  me: () => request<any>('/api/auth/me'),
  logout: () => request<any>('/api/auth/logout', { method: 'POST' }),
  changePassword: (old_password: string, new_password: string) =>
    request<any>('/api/auth/change-password', { method: 'POST', body: { old_password, new_password } }),

  // Nodes
  getPrompt: (role: string) => request<any>(`/api/nodes/prompts/${role}`),
  updatePrompt: (role: string, prompt: string) =>
    request<any>(`/api/nodes/prompts/${role}`, { method: 'PUT', body: { prompt } }),
  resetPrompt: (role: string) =>
    request<any>(`/api/nodes/prompts/${role}`, { method: 'DELETE' }),

  // Provider config
  getProviderConfig: () => request<any>('/api/admin/provider-config'),
  updateNodeChain: (node_role: string, chain: any[]) =>
    request<any>('/api/admin/provider-config/node', { method: 'POST', body: { node_role, chain } }),
  updateGlobalApiKeys: (api_keys: Record<string, string>) =>
    request<any>('/api/admin/provider-config/keys', { method: 'POST', body: { api_keys } }),
  testProvider: (data: any) =>
    request<any>('/api/admin/provider-config/test', { method: 'POST', body: data }),
  getModelRegistry: () => request<any>('/api/admin/provider-config/models'),

  // Projects
  listProjects: () => request<any>('/api/projects'),
  getProjectTree: (id: string, prefix = '') =>
    request<any>(`/api/projects/${id}/tree?prefix=${encodeURIComponent(prefix)}`),
  getFileContent: (id: string, path: string) =>
    request<any>(`/api/projects/${id}/files/${path}`),
  getProjectStatus: (id: string) =>
    request<any>(`/api/projects/${id}/status`),
  deleteProject: (id: string) =>
    request<any>(`/api/projects/${id}`, { method: 'DELETE' }),

  // API Keys
  listApiKeys: () => request<any>('/api/api-keys'),
  createApiKey: (name: string) =>
    request<any>('/api/api-keys', { method: 'POST', body: { name } }),
  revokeApiKey: (id: string) =>
    request<any>(`/api/api-keys/${id}`, { method: 'DELETE' }),

  // Stats
  getStats: () => request<any>('/api/stats'),

  // User Profile
  getProfile: () => request<any>('/api/user/me'),

  // Chat History (Cloud RAG Projects)
  getProjectChatHistory: (projectId: string) => request<{ conversations: any[] }>(`/api/projects/${projectId}/history`),
  saveProjectChatHistory: (projectId: string, conversations: any[]) => request<any>(`/api/projects/${projectId}/history`, { method: 'POST', body: { conversations } }),


  // Admin Users
  adminGetUsers: () => request<any>('/api/admin/users'),
  adminCreateUser: (user: any) => request<any>('/api/admin/users', { method: 'POST', body: user }),
  adminUpdateUser: (id: string, user: any) => request<any>(`/api/admin/users/${id}`, { method: 'PUT', body: user }),
  adminDeleteUser: (id: string) => request<any>(`/api/admin/users/${id}`, { method: 'DELETE' }),
  adminAssignPlan: (user_id: string, plan_id: string) => request<any>('/api/admin/users/plan', { method: 'POST', body: { user_id, plan_id } }),

  // Admin Plans
  adminGetPlans: () => request<any>('/api/admin/plans'),
  adminCreatePlan: (plan: any) => request<any>('/api/admin/plans', { method: 'POST', body: plan }),
  adminDeletePlan: (id: string) => request<any>(`/api/admin/plans/${id}`, { method: 'DELETE' }),

  // Image Upload
  uploadImage: async (file: File) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/api/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<{ url: string; type?: string; content?: string }>;
  },
};

export { request as apiFetch };

export function getLocalAgentConfig() {
  const s = localStorage.getItem('eric_mec_active_agent');
  if (!s) return null;
  try { return JSON.parse(s); } catch(e) { return null; }
}
